import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DatePickerMobi,
  FilterIcon,
  Grid,
  OperatorTreeSelect,
  Stack,
  Box,
  Typography,
  TextField,
  IconButton,
  ToolTip,
  Chip,
  WarningThinIcon
} from '@esgian/esgianui';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  getOngoingVoyagesFilters,
  updateOngoingVoyagesFilters
} from '@store/features/filters/VoyageAnalyticsPage/OngoingVoyage/OngoingVoyagesSlice';
import { DATE_FORMAT } from '@constants';
import AdvancedOngoingVoyageAnalyticsFilters from '@components/Sections/ShipAnalytics/VoyageAnalyticsSection/OngoingVoyageAnalyticsFilters/AdvancedOngoingVoyageAnalyticsFilters';
import { TextWithTooltipIcon } from '@components';
import {
  useLookupOperatorVesselHierarchyQuery,
  useLookupOwnerHierarchyQuery,
  useLookupVesselsQuery
} from '@hooks';

// Custom swap icon since SwapHorizIcon isn't available
const SwitchModeIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"
      fill={color}
    />
  </svg>
);

SwitchModeIcon.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string
};

function OngoingVoyageAnalyticsFilters({ checkValidFilters, handleApplyFilters }) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [inputMode, setInputMode] = useState('tree'); // 'tree' or 'imo'
  const [imoInput, setImoInput] = useState('');
  const [imoError, setImoError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const dispatch = useDispatch();
  const reduxFilters = useSelector(getOngoingVoyagesFilters);
  const [tempSelectedFilters, setTempSelectedFilters] = useState({});
  const { data: lookupOwnerHierarchy } = useLookupOwnerHierarchyQuery();

  const { data: lookupOperatorVesselHierarchy } = useLookupOperatorVesselHierarchyQuery();
  const { data: lookupVessels } = useLookupVesselsQuery();

  const { startDate } = tempSelectedFilters;
  const yesterday = moment().startOf('day').subtract(1, 'day');

  const [invalidImos, setInvalidImos] = useState([]);

  useEffect(() => {
    setTempSelectedFilters(reduxFilters);
  }, [reduxFilters]);

  useEffect(() => {
    if (!tempSelectedFilters.startDate) {
      setTempSelectedFilters((prev) => ({ ...prev, startDate: yesterday.format(DATE_FORMAT) }));
    }
  }, [tempSelectedFilters]);

  const updateFilterValue = (payload) => {
    setTempSelectedFilters((prev) => ({ ...prev, ...payload }));
  };

  const handleApply = (filters = {}) => {
    const updated = { ...tempSelectedFilters, ...filters };
    dispatch(updateOngoingVoyagesFilters(updated));
    handleApplyFilters(updated);
  };

  const handleImoSubmit = (imoList) => {
    if (!imoList.trim()) {
      setImoError('Please enter at least one IMO number');
      setInvalidImos([]);
      return;
    }

    // Clear previous debug info
    setDebugInfo('');

    // Parse IMO numbers from input (handling multiple formats like comma-separated, newlines, spaces)
    const imoNumbers = imoList
      .split(/[\s,;]+/)
      .map((imo) => imo.trim())
      .filter((imo) => imo.length > 0);

    // Log IMO numbers being processed
    setDebugInfo((prev) => prev + `IMO numbers parsed: ${imoNumbers.join(', ')}\n`);

    // Check if vessels exist in the system
    if (
      !vesselHierarchyOptions ||
      !vesselHierarchyOptions.vessels ||
      !vesselHierarchyOptions.vessels.length
    ) {
      setImoError('Vessel data is not loaded yet. Please try again later.');
      setInvalidImos([]);
      return;
    }

    // Log first few vessel IMOs for comparison
    const sampleVessels = vesselHierarchyOptions.vessels.slice(0, 5);
    setDebugInfo(
      (prev) =>
        prev +
        `Sample vessels in system: ${JSON.stringify(
          sampleVessels.map((v) => ({ id: v.id, name: v.name }))
        )}\n`
    );

    // Try to match the IMO numbers with vessels in the system
    const existingImos = [];
    const notFoundImos = [];

    imoNumbers.forEach((imo) => {
      // Try to find the vessel with this IMO
      const matchingVessel = vesselHierarchyOptions.vessels.find(
        (vessel) => vessel.id === imo || vessel.id === Number(imo) || String(vessel.id) === imo
      );

      if (matchingVessel) {
        existingImos.push(matchingVessel.id);
      } else {
        notFoundImos.push(imo);
      }
    });

    // Log match results
    setDebugInfo((prev) => prev + `Found matches: ${existingImos.join(', ')}\n`);
    setDebugInfo((prev) => prev + `Not found: ${notFoundImos.join(', ')}\n`);

    if (existingImos.length === 0) {
      setImoError('No valid IMO numbers found. Please check your input and try again.');
      setInvalidImos(notFoundImos);
      return;
    }

    // If we have previously selected operators, keep them
    const operatorIds = tempSelectedFilters?.operatorIds || [];

    // Update filters with new IMOs
    updateFilterValue({
      imos: existingImos,
      operatorIds: operatorIds.length ? operatorIds : null
    });

    // Update invalid IMOs list
    setInvalidImos(notFoundImos);
    setImoError(
      notFoundImos.length > 0
        ? `${notFoundImos.length} invalid IMO number${notFoundImos.length > 1 ? 's' : ''} found.`
        : ''
    );
  };

  const toggleInputMode = () => {
    setInputMode(inputMode === 'tree' ? 'imo' : 'tree');
    setImoError('');
    setDebugInfo('');
    setInvalidImos([]);
  };

  const vesselHierarchyOptions = useMemo(() => {
    if (
      lookupOwnerHierarchy.length &&
      lookupOperatorVesselHierarchy.length &&
      lookupVessels.length
    ) {
      return {
        ownerHierarchy: lookupOwnerHierarchy,
        operatorHierarchy: lookupOperatorVesselHierarchy,
        vessels: lookupVessels
      };
    }
    return null;
  }, [lookupOwnerHierarchy, lookupOperatorVesselHierarchy, lookupVessels]);

  const defaultSelectedOperatorVessel = useMemo(() => {
    const selectedImoArray = tempSelectedFilters?.imos;
    if (!vesselHierarchyOptions || !selectedImoArray || selectedImoArray.length === 0) {
      return [];
    }
    const selectedOperatorIds = tempSelectedFilters?.operatorIds ?? [];

    const selected = [];

    selectedImoArray.forEach((imo) => {
      const vessel = vesselHierarchyOptions.vessels.find(({ id }) => id === imo);
      if (!vessel) {
        return;
      }
      selected.push(vessel);
    });

    selectedOperatorIds.forEach((operatorId) => {
      const operator = vesselHierarchyOptions.operatorHierarchy.find(({ id }) => id === operatorId);
      if (!operator) {
        return;
      }
      selected.push(operator);
    });

    return selected;
  }, [vesselHierarchyOptions, tempSelectedFilters]);

  return (
    <Grid item xs={12}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Stack className={'section-filter'}>
            <Box sx={{ position: 'relative', display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                <Typography variant="body2">Select Operators/Vessels*</Typography>
                <ToolTip
                  title={
                    inputMode === 'tree' ? 'Switch to IMO paste mode' : 'Switch to tree selection'
                  }>
                  <IconButton
                    onClick={toggleInputMode}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)'
                      }
                    }}>
                    <SwitchModeIcon />
                  </IconButton>
                </ToolTip>
              </Box>

              <Box
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  width: '360px'
                }}>
                {inputMode === 'tree' ? (
                  <OperatorTreeSelect
                    selected={defaultSelectedOperatorVessel}
                    handleClose={(value) => {
                      let res = {};
                      let selected = value?.selected || [];
                      const vesselImos = selected
                        ?.filter(({ type }) => type === 'vessel')
                        .map(({ id }) => id);

                      const operatorId = selected
                        ?.filter(({ type }) => type === 'operator')
                        .map(({ id }) => id);

                      res.imos = vesselImos.length ? vesselImos : null;
                      res.operatorIds = operatorId.length ? operatorId : null;

                      updateFilterValue(res);
                    }}
                    handleSelectTypeChange={() => {}}
                    lookupVesselHierarchy={vesselHierarchyOptions}
                    loading={!vesselHierarchyOptions}
                    sx={{
                      minWidth: '300px',
                      border: 'none'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <TextField
                        type={''}
                        fullWidth
                        value={imoInput}
                        onChange={(e) => {
                          setImoInput(e.target.value);
                          if (imoError) setImoError('');
                          if (invalidImos.length) setInvalidImos([]);
                          if (debugInfo) setDebugInfo('');
                        }}
                        onBlur={(e) => handleImoSubmit(e.target.value)}
                        placeholder="Enter IMO numbers separated by commas"
                        error={!!imoError}
                        helperText={imoError}
                        sx={{ width: '360px', maxWidth: '360px' }}
                        variant="outlined"
                        InputProps={{
                          endAdornment: invalidImos.length > 0 && (
                            <ToolTip
                              title={
                                <Box sx={{ p: 1, maxWidth: 300 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 'bold', color: 'white' }}>
                                    Invalid IMO numbers:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {invalidImos.map((imo, index) => (
                                      <Chip
                                        key={index}
                                        label={imo}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                          color: 'white',
                                          border: '1px solid rgba(255, 255, 255, 0.3)'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              }
                              placement="right">
                              <IconButton
                                size="small"
                                sx={{
                                  color: '#d32f2f',
                                  padding: '2px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                  }
                                }}>
                                <WarningThinIcon fontSize="small" />
                              </IconButton>
                            </ToolTip>
                          )
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>

          <Stack className={'section-filter'}>
            <DatePickerMobi
              sx={{
                inputProps: {
                  InputProps: {},
                  defaultValue: null,
                  value: startDate ? moment(startDate, DATE_FORMAT).format(DATE_FORMAT) : '-',
                  placeholder: 'Period',
                  label: (
                    <TextWithTooltipIcon
                      color={'inherit'}
                      labelVariant={'inherit'}
                      labelBold={false}
                      label={'From'}
                      tooltipText={
                        'How Long back do you want the data from? Currently supports up to 100 days data going back from today'
                      }
                    />
                  ),
                  autoComplete: 'off'
                }
              }}
              selectedDate={startDate ? moment(`${startDate}`) : null}
              onClose={({ value }) => {
                if (moment(value).format(DATE_FORMAT) !== moment(startDate).format(DATE_FORMAT)) {
                  updateFilterValue({ startDate: moment(value).format(DATE_FORMAT) });
                }
              }}
              dateFormat={DATE_FORMAT}
              minStartDate={moment().subtract(100, 'day')}
              dateWheels={DATE_FORMAT}
              maxDate={yesterday}
            />
          </Stack>

          <Button
            disabled={!checkValidFilters(tempSelectedFilters)}
            onClick={() => {
              handleApply();
            }}
            variant={'contained'}
            color={'primary'}
            sx={{ height: 'min-content' }}>
            Apply Filters
          </Button>
        </Stack>

        <Button
          sx={{ height: 'min-content' }}
          startIcon={<FilterIcon />}
          variant={'text'}
          disabled={inputMode === 'imo'}
          onClick={() => setAdvancedOpen(true)}>
          Advanced Filter
        </Button>
      </Stack>

      <AdvancedOngoingVoyageAnalyticsFilters
        updateFilterValue={updateFilterValue}
        advancedOpen={advancedOpen}
        setAdvancedOpen={setAdvancedOpen}
        handleApplyFilters={handleApply}
        disableApply={!checkValidFilters(tempSelectedFilters)}
      />
    </Grid>
  );
}

OngoingVoyageAnalyticsFilters.propTypes = {
  checkValidFilters: PropTypes.func.isRequired,
  handleApplyFilters: PropTypes.func.isRequired
};

OngoingVoyageAnalyticsFilters.defaultProps = {};

export default OngoingVoyageAnalyticsFilters;
